import React, { useMemo } from "react";
import { orderBy } from "lodash";

import TemplateCard from "../components/templates/TemplateCard";
import useTranslation from "../hooks/useTranslations";
import useGetTemplates from "../hooks/useGetTemplates";
import useViewer from "../hooks/useViewer";
import { TemplateIcon } from "../components/icons";
import {
  Container,
  ScreenContainer,
  ScreenHeader,
  Link,
  EmptyState,
} from "../components/wombat-ui";
import { useHistory } from "react-router-dom";
import { H3 } from "../components/wombat-ui/components/Typography";

const TemplateListScreen = () => {
  const t = useTranslation();
  const { isCapableOfInsertingTemplates } = useViewer();

  const { data, loading, error, subscribeToMore } = useGetTemplates({
    variables: { madId: null },
  });

  const templates = data?.templates?.collection || [];

  const orderedTemplate = useMemo(
    () =>
      orderBy(templates, (template) => new Date(template.updatedAt), "desc"),
    [templates]
  );

  const defaultTemplates = useMemo(
    () => orderedTemplate.filter((t) => t.default),
    [orderedTemplate]
  );

  const customTemplates = useMemo(
    () => orderedTemplate.filter((t) => !t.default),
    [orderedTemplate]
  );

  return (
    <ScreenContainer
      loading={loading}
      error={
        !isCapableOfInsertingTemplates ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link variant="button" to="/">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      <ScreenHeader
        title={t("templates.templates") as string}
        description={t("templates.templatesDescription") as string}
        icon={TemplateIcon}
        actions={[
          isCapableOfInsertingTemplates && (
            <Link variant="button" to="/templates/new" key="new">
              {t("templates.createTemplate")}
            </Link>
          ),
        ]}
      />
      <Container className="flex flex-col space-y-10">
        {defaultTemplates.length > 0 && (
          <div>
            <div>
              <H3 description={t("templates.defaultTemplatesListDescription")}>
                {t("templates.defaultTemplatesList")}
              </H3>
            </div>
            <div className="grid gap-8 px-0 py-6 overflow-x-auto grid-cols-scrollable lg:grid-cols-3">
              {defaultTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          </div>
        )}
        {customTemplates.length > 0 && (
          <div className="space-y-8">
            <div>
              <H3 description={t("templates.customTemplatesListDescription")}>
                {t("templates.customTemplatesList")}
              </H3>
            </div>
            <div className="grid gap-8 px-0 overflow-x-auto grid-cols-scrollable lg:grid-cols-3">
              {customTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          </div>
        )}
      </Container>
    </ScreenContainer>
  );
};

export default TemplateListScreen;
